import React, { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import QRCodeErrorModal from "./modals/QRCodeErrorModal";
import ToastMessage from "./modals/ToastMsg";
import { verifyCode } from "./services/verificationService";
import LoadingSpinner from "./modals/LoadingSpinner";
import { logInvalidScanEvent } from "./services/LogScanEvent";
import { useNavigate } from 'react-router-dom';

export default function Scanner() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [result, setResult] = useState("Waiting for QR code...");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const scanningRef = useRef(true);
  const navigate = useNavigate();
  const [isVerifying, setIsVerifying] = useState(false);
  const [showRetryButton, setShowRetryButton] = useState(false);

  // Start the camera and begin scanning
  const startCamera = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        ToastMessage.error("Camera not supported by this browser.");
        return;
      }

      // Assign the stream to the ref
      streamRef.current = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });

      if (!videoRef.current) return;

      videoRef.current.srcObject = streamRef.current;

      videoRef.current.onloadedmetadata = () => {
        videoRef.current.play();
        requestAnimationFrame(scan);
      };
    } catch (err) {
      if (err.name === "NotAllowedError") {
        ToastMessage.error("Camera access denied by user.");
      } else if (err.name === "NotFoundError") {
        ToastMessage.error("No camera found on this device.");
      } else if (err.name === "NotReadableError") {
        ToastMessage.error("Camera is already in use.");
      } else {
        ToastMessage.error("Unable to access the camera.");
      }
    }
  };

  // Scan for QR codes using canvas
  const scan = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Check if video and canvas are properly initialized
    if (!video || !canvas) return;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    if (!scanningRef.current) return;

    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw the video frame to the canvas
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height);

      if (code) {
        const scannedData = code.data.trim();
        scanningRef.current = false;
        stopCamera();
        handleScannedCode(scannedData);
        return;
      } else {
        setResult("Scanning for QR code...");
      }
    }

    requestAnimationFrame(scan);
  };

  // Function to stop the camera
  const stopCamera = () => {
    const video = videoRef.current;
    if (video && video.srcObject) {
      const stream = streamRef.current;  // Access the stream from the ref
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    }
  };

  useEffect(() => {
    // Start the camera when the component mounts
    startCamera();

    // Clean up by stopping the camera on unmount
    return () => {
      stopCamera();
    };
  }, []);

  const closeModal = () => {
    setShowErrorModal(false);
    setResult("Waiting for QR code...");
    scanningRef.current = true;
    startCamera();
  };

  const handleScannedCode = async (uniqueCode) => {
    try {
      setIsVerifying(true);
      setResult("Processing scanned code...");

      // Try parsing the scanned code as JSON
      let parsedCode;
      try {
        parsedCode = JSON.parse(uniqueCode);
      } catch (parseError) {
        setResult("Invalid QR Code...");
        logInvalidScanEvent();
        setShowErrorModal(true);
        return;
      }

      // Validate required fields
      const { encrypted_uuid, iv } = parsedCode || {};
      if (!encrypted_uuid || !iv) {
        setResult("Invalid QR Code...");
        logInvalidScanEvent();
        setShowErrorModal(true);
        return;
      }

      // Simulate loading
      setResult("Verifying code...");
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Call backend
      const data = await verifyCode(parsedCode);

      // Handle verification result
      switch (data.status) {
        case "Verified":
          ToastMessage.success(data.message || "Code is verified!");

          if (data.htmlContent) {
            navigate("/result", {
              state: { html: data.htmlContent },
            });
          }
          break;

        case "Not Verified":
          ToastMessage.error(data.message || "Code is not verified.");

          if (data.htmlContent) {
            navigate("/result", {
              state: { html: data.htmlContent },
            });
          }
          break;

        case "Unknown":
          setShowErrorModal(true);
          break;

        default:
          ToastMessage.warning("Unexpected verification result.");
          setShowRetryButton(true);
      }
    } catch (error) {
      ToastMessage.error("Verification failed. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleRetry = () => {
    setShowRetryButton(false);
    setResult("Waiting for QR code...");
    scanningRef.current = true;
    startCamera();
  };

  return (
    <main className="flex-1 flex flex-col items-center px-4 py-6">
      {/* Hidden Video */}
      <video ref={videoRef} autoPlay playsInline preload="auto" className="hidden" />

      {/* Scanner Canvas */}
      <canvas
        ref={canvasRef}
        className="w-full max-w-xl h-64 sm:h-72 md:h-80 lg:h-96 bg-white border-2 border-gray-400 shadow-md mb-2"
      >
        Your browser does not support the canvas element.
      </canvas>

      {/* QR Result */}
      <p className="text-gray-700 font-medium text-center mt-2">{result}</p>

      {/* Instruction Message */}
      <div className="text-center text-gray-700 text-base sm:text-lg px-2 mt-2">
        Please place the QR code in the scanner window.
      </div>

      {/* Retry Button */}
      {showRetryButton && (
        <button
          onClick={handleRetry}
          style={{ backgroundColor: '#103D4B' }}
          className="hover:bg-blue-700 text-white px-6 py-2 mt-2 rounded transition"
        >
          Retry Scanning
        </button>
      )}

      {/* Modal for Invalid QR */}
      <div data-testid="qr-error-modal"> 
        {showErrorModal && <QRCodeErrorModal onClose={closeModal} />}
      </div>

      {/* Modal for the Loading Spinner */}
      <div data-testid="loading-spinner">
        {isVerifying && <LoadingSpinner message="Verifying..." />}
      </div>
      
    </main>
  );
};